import React, { useRef } from "react"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
import parseNum from "parse-num"
import Highchart from "components/charts/Highchart"
import theme from "styles/theme"
import useElementDimensions from "hooks/useElementDimensions"

const PieChart = ({ seriesLabel, data, scale, options = { chart: {} } }) => {
  const containerRef = useRef()
  const { width = 500 } = useElementDimensions(containerRef) || {}
  const condensedView = width < 320

  return (
    <div ref={containerRef}>
      <Highchart
        options={{
          ...options,
          series: [
            { name: seriesLabel, data: prepareData(data), colorByPoint: true },
          ],
          chart: {
            ...options.chart,
            type: "pie",
          },
          tooltip: {
            pointFormat:
              seriesLabel === "Percentage"
                ? `<b>{point.percentage:.${scale}f}%</b>`
                : `<b>{point.y:.${scale}f}</b> ({point.percentage:.${scale}f}%)
                  <br />
                  {series.name}
                `,
            backgroundColor: theme.white,
            style: {},
          },
          legend: condensedView
            ? {
                enabled: true,
                align: "center",
                itemMarginBottom: 10,
                itemStyle: {
                  fontSize: 12,
                  fontWeight: 400,
                  color: theme.gray,
                },
              }
            : {},
          plotOptions: {
            pie: {
              cursor: "pointer",
              showInLegend: condensedView,
              dataLabels: {
                enabled: !condensedView,
                format:
                  seriesLabel === "Percentage"
                    ? `<b>{point.name}</b><br />{point.percentage:.${scale}f}%`
                    : `<b>{point.name}</b>:<br />{point.y:.${scale}f}`,
                distance: 15,
                connectorColor: theme.lightGray,
                style: {
                  fontSize: 12,
                  fontWeight: 400,
                  color: theme.gray,
                  textOutline: "none",
                },
              },
              innerSize: "55%",
            },
          },
        }}
      />
    </div>
  )
}

PieChart.propTypes = {
  seriesLabel: PropTypes.string.isRequired,
  scale: PropTypes.oneOf(["0", "1", "2", "3"]).isRequired,
  data: PropTypes.shape({
    rows: PropTypes.arrayOf(
      PropTypes.shape({ cells: PropTypes.array.isRequired }).isRequired
    ).isRequired,
  }).isRequired,
  options: PropTypes.shape({
    chart: PropTypes.object.isRequired,
  }),
}

export default PieChart

const prepareData = data =>
  data.rows.map(({ cells: [name, y] }) => ({
    name,
    y: parseNum(y),
  }))

export const query = graphql`
  fragment PieChart on SanityPieChart {
    type: _type
    layout
    scale
    title
    subtitle
    seriesLabel
    data {
      rows {
        cells
      }
    }
    footnotes
  }
`
